import React from "react";
import uniqueId from "lodash/uniqueId";
import { FieldArray } from "formik";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import { format, isValid } from "date-fns";

import { ArrayRemoveButton } from "../Buttons/ArrayRemoveButton";
import { ArrayAddButton } from "../Buttons/ArrayAddButton";
import { Divider } from "../../../../components/Divider";
import {
  Checkbox,
  Input,
  SelectAutocomplete,
  AutoSaveField as Field,
  DatePicker
} from "../../../../components/Form";
import {
  limits,
  initialEntitiesInUAE,
  initialEntitiesOutsideUAE,
  MAX_COMPANY_NAME_LENGTH,
  MAX_TRADE_LICENSE_LENGTH
} from "./constants";

import { getCountriesOtherthanUAE } from "../../../../utils/helper";
import { GroupLabel, RelativeBox, FlexContainer, FlexItem, FlexActionItem } from "./styled";
import { YEAR_FORMAT, DATE_FORMAT } from "../../../../constants";
import PropTypes from "prop-types";

export const FiftyYearsAgo = new Date();
FiftyYearsAgo.setFullYear(FiftyYearsAgo.getFullYear() - 50, 0, 1);

export const CompanyBranchesAndSubsidiariesComponent = ({
  entitiesInUAE,
  entitiesOutsideUAE,
  updateProspect,
  isComeFromROScreens,
  values,
  setTouched,
  setFieldValue
}) => {
  const today = React.useMemo(() => new Date(), []);
  const basisPath = "prospect.orgKYCDetails";

  const changeDateProspectHandler = (_, value, path) =>
    isValid(value) && { [path]: format(value, YEAR_FORMAT) };
  const changeDateFormatProspectHandler = (_, value, path) =>
    isValid(value) && { [path]: format(value, DATE_FORMAT) };

  const isEntitiesInUAE = values.entitiesInUAE.length > 1;
  const isEntitiesOutsideUAE = values.entitiesOutsideUAE.length > 1;
  return (
    <React.Fragment>
      <FieldArray name="entitiesInUAE">
        {arrayHelpers => (
          <>
            <Grid container sx={{ mt: 0, rowGap: "16px" }}>
              <Grid item xs={12}>
                <GroupLabel>In the UAE</GroupLabel>
              </Grid>
              <Grid item xs={12}>
                <Field
                  name="otherEntitiesInUAE"
                  path="prospect.orgKYCDetails.otherEntitiesInUAE"
                  label="Company or shareholders have other companies, branches or subsidiaries in the UAE"
                  component={Checkbox}
                  onSelect={() => {
                    if (values.otherEntitiesInUAE) {
                      setFieldValue(
                        "entitiesInUAE",
                        initialEntitiesInUAE.map(item => ({
                          ...item,
                          id: uniqueId()
                        }))
                      );
                      updateProspect({
                        "prospect.orgKYCDetails.entitiesInUAE": initialEntitiesInUAE
                      });
                      setTouched({
                        "entitiesInUAE[0].companyName": false,
                        "entitiesInUAE[0].tradeLicenseNo": false,
                        "entitiesInUAE[0].emirate": false,
                        "entitiesInUAE[0].tradeLicenseExpiryDate": false,
                        "entitiesInUAE[0].tradeLicenseIssueDate": false
                      });
                    }
                  }}
                  inputProps={{ tabIndex: 0 }}
                />
              </Grid>
            </Grid>
            {values.otherEntitiesInUAE && (
              <>
                {values.entitiesInUAE.map((item, index) => (
                  <Box key={item.id}>
                    <FlexContainer sx={{ alignItems: "flex-start" }}>
                      <FlexItem>
                        <Grid item container spacing={3} sx={{ mt: 0 }}>
                          <Grid item xs={12} sx={{ pt: "0px !important" }}>
                            <Field
                              name={`entitiesInUAE[${index}].companyName`}
                              path={`${basisPath}.entitiesInUAE[${index}].companyName`}
                              label="Company name"
                              placeholder="Company name"
                              component={Input}
                              contextualHelpText={
                                <>
                                  The name of the other company, branch or subsidiary should be as
                                  per their respective Trade license.
                                  <br /> Example <br /> Airlift Global Automation and Heavy
                                  Equipment Rental LLC
                                </>
                              }
                              InputProps={{
                                inputProps: { maxLength: MAX_COMPANY_NAME_LENGTH, tabIndex: 0 }
                              }}
                            />
                          </Grid>
                        </Grid>
                        <Grid item container spacing={3} sx={{ mt: { xs: "16px", sm: "24px" } }}>
                          <Grid item sm={6} xs={12} sx={{ pt: "0px !important" }}>
                            <Field
                              name={`entitiesInUAE[${index}].emirate`}
                              path={`prospect.orgKYCDetails.entitiesInUAE[${index}].emirate`}
                              datalistId="emirateCity"
                              label="Emirate/ City"
                              isSearchable
                              component={SelectAutocomplete}
                              tabIndex="0"
                            />
                          </Grid>
                          <Grid
                            item
                            sm={6}
                            xs={12}
                            sx={{ pt: { xs: "16px !important", sm: "0px !important" } }}
                          >
                            <Field
                              name={`entitiesInUAE[${index}].tradeLicenseNo`}
                              path={`${basisPath}.entitiesInUAE[${index}].tradeLicenseNo`}
                              label="Trade License Number"
                              placeholder="Trade License Number"
                              component={Input}
                              contextualHelpText="The License Number of the other company, branch or subsidiary
                                    should be as per their respective Trade License."
                              InputProps={{
                                inputProps: { maxLength: MAX_TRADE_LICENSE_LENGTH, tabIndex: 0 }
                              }}
                            />
                          </Grid>
                        </Grid>
                        <Grid item container spacing={3} sx={{ mt: { xs: "16px", sm: "24px" } }}>
                          <Grid item sm={6} xs={12} sx={{ pt: "0px !important" }}>
                            <Field
                              name={`entitiesInUAE[${index}].tradeLicenseIssueDate`}
                              path={`${basisPath}.entitiesInUAE[${index}].tradeLicenseIssueDate`}
                              label="Year of TL Issued"
                              isSearchable
                              component={DatePicker}
                              placeholder="____"
                              minDate={FiftyYearsAgo}
                              openTo="year"
                              views={["year"]}
                              format="yyyy"
                              inputProps={{ tabIndex: 0 }}
                              changeProspect={changeDateProspectHandler}
                              maxDate={new Date()}
                            />
                          </Grid>
                          <Grid
                            item
                            sm={6}
                            xs={12}
                            sx={{ pt: { xs: "16px !important", sm: "0px !important" } }}
                          >
                            <Field
                              name={`entitiesInUAE[${index}].tradeLicenseExpiryDate`}
                              path={`${basisPath}.entitiesInUAE[${index}].tradeLicenseExpiryDate`}
                              label="Year of TL Expiry"
                              isSearchable
                              component={DatePicker}
                              placeholder="____"
                              openTo="year"
                              views={["year"]}
                              format="yyyy"
                              inputProps={{ tabIndex: 0 }}
                              changeProspect={changeDateProspectHandler}
                              minDate={today}
                              disableFuture={false}
                            />
                          </Grid>
                        </Grid>
                        <Grid item container spacing={3} sx={{ mt: { xs: "16px", sm: "24px" } }}>
                          <Grid item xs={12} sx={{ pt: "0px !important" }}>
                            <Field
                              name={`entitiesInUAE[${index}].natureOfBusiness`}
                              path={`${basisPath}.entitiesInUAE[${index}].natureOfBusiness`}
                              label="Nature of Business (Optional)"
                              placeholder="Nature of Business"
                              datalistId="natureOfBusiness"
                              isSearchable
                              component={SelectAutocomplete}
                              tabIndex="0"
                            />
                          </Grid>
                        </Grid>
                        {index < values.entitiesInUAE.length - 1 && <Divider.Dark sx={{ mb: 0 }} />}
                      </FlexItem>
                      {isEntitiesInUAE && (
                        <FlexActionItem>
                          <ArrayRemoveButton
                            arrayHelpers={arrayHelpers}
                            dataArray={entitiesInUAE}
                            itemIndex={index}
                            updateProspect={updateProspect}
                            prospectPath="prospect.orgKYCDetails.entitiesInUAE"
                            title="Delete"
                          />
                        </FlexActionItem>
                      )}
                    </FlexContainer>
                  </Box>
                ))}
                {values.entitiesInUAE.length < limits.INSIDE_SUBSIDIARY_COUNT && (
                  <RelativeBox component={Grid} item sm={3} xs={12}>
                    <ArrayAddButton
                      title="Add another"
                      limit={limits.INSIDE_SUBSIDIARY_COUNT}
                      requiredFields={["companyName", "tradeLicenseNo"]}
                      addedItem={{
                        companyName: "",
                        tradeLicenseNo: "",
                        emirate: "",
                        tradeLicenseIssueYear: "",
                        tradeLicenseExpiryYear: ""
                      }}
                      dataArray={values.entitiesInUAE}
                      arrayHelpers={arrayHelpers}
                    />
                  </RelativeBox>
                )}
              </>
            )}
          </>
        )}
      </FieldArray>
      <FieldArray name="entitiesOutsideUAE">
        {arrayHelpers => (
          <>
            <Grid container sx={{ mt: 0, rowGap: "16px" }}>
              <Grid item xs={12}>
                <GroupLabel>Outside the UAE</GroupLabel>
              </Grid>
              <Grid item xs={12}>
                <Field
                  name="otherEntitiesOutsideUAE"
                  path="prospect.orgKYCDetails.otherEntitiesOutsideUAE"
                  label="Company or shareholders have other companies, branches or subsidiaries outside the UAE"
                  component={Checkbox}
                  onSelect={() => {
                    if (values.otherEntitiesOutsideUAE) {
                      setFieldValue(
                        "entitiesOutsideUAE",
                        initialEntitiesOutsideUAE.map(item => ({
                          ...item,
                          id: uniqueId()
                        }))
                      );
                      updateProspect({
                        "prospect.orgKYCDetails.entitiesOutsideUAE": initialEntitiesOutsideUAE
                      });
                      setTouched({
                        "entitiesOutsideUAE[0].companyName": false,
                        "entitiesOutsideUAE[0].country": false,
                        "entitiesOutsideUAE[0].tlIssueDate": false
                      });
                    }
                  }}
                  inputProps={{ tabIndex: 0 }}
                />
              </Grid>
            </Grid>
            {values.otherEntitiesOutsideUAE && (
              <>
                {values.entitiesOutsideUAE.map((item, index) => (
                  <Box key={item.id}>
                    <FlexContainer sx={{ alignItems: "flex-start" }}>
                      <FlexItem>
                        <Grid item container spacing={3} sx={{ mt: 0 }}>
                          <Grid item sm={6} xs={12} sx={{ pt: "0px !important" }}>
                            <Field
                              name={`entitiesOutsideUAE[${index}].companyName`}
                              path={`${basisPath}.entitiesOutsideUAE[${index}].companyName`}
                              label="Company name"
                              placeholder="Company name"
                              component={Input}
                              contextualHelpText={
                                <>
                                  The name of the other company, branch or subsidiary should be as
                                  per their respective registration documents.
                                  <br /> Example <br /> Airlift Global Automation and Heavy
                                  Equipment Rental LLC
                                </>
                              }
                              InputProps={{
                                inputProps: { maxLength: MAX_COMPANY_NAME_LENGTH, tabIndex: 0 }
                              }}
                            />
                          </Grid>
                          <Grid
                            item
                            sm={6}
                            xs={12}
                            sx={{ pt: { xs: "16px !important", sm: "0px !important" } }}
                          >
                            <Field
                              name={`entitiesOutsideUAE[${index}].country`}
                              filterOptions={countries => getCountriesOtherthanUAE(countries)}
                              path={`${basisPath}.entitiesOutsideUAE[${index}].country`}
                              datalistId="countryOfIncorporation"
                              label="Country"
                              component={SelectAutocomplete}
                              tabIndex="0"
                            />
                          </Grid>
                        </Grid>
                        {isComeFromROScreens && (
                          <Grid item container spacing={3} sx={{ mt: { xs: "16px", sm: "24px" } }}>
                            <Grid item xs={12} sx={{ pt: "0px !important" }}>
                              <Field
                                name={`entitiesOutsideUAE[${index}].tlIssueDate`}
                                filterOptions={countries => getCountriesOtherthanUAE(countries)}
                                path={`${basisPath}.entitiesOutsideUAE[${index}].tlIssueDate`}
                                label="Date of TL Issued"
                                component={DatePicker}
                                InputProps={{
                                  inputProps: { tabIndex: 0 }
                                }}
                                maxDate={new Date()}
                                changeProspect={changeDateFormatProspectHandler}
                              />
                            </Grid>
                          </Grid>
                        )}
                        <Grid item container spacing={3} sx={{ mt: { xs: "16px", sm: "24px" } }}>
                          <Grid item xs={12} sx={{ pt: "0px !important" }}>
                            <Field
                              name={`entitiesOutsideUAE[${index}].natureOfBusiness`}
                              path={`${basisPath}.entitiesOutsideUAE[${index}].natureOfBusiness`}
                              label="Nature of Business (Optional)"
                              placeholder="Nature of Business"
                              datalistId="natureOfBusiness"
                              isSearchable
                              component={SelectAutocomplete}
                              tabIndex="0"
                            />
                          </Grid>
                        </Grid>
                        {index < values.entitiesOutsideUAE.length - 1 && (
                          <Divider.Dark sx={{ mb: 0 }} />
                        )}
                      </FlexItem>
                      {isEntitiesOutsideUAE && (
                        <FlexActionItem>
                          <ArrayRemoveButton
                            arrayHelpers={arrayHelpers}
                            dataArray={entitiesOutsideUAE}
                            itemIndex={index}
                            updateProspect={updateProspect}
                            prospectPath="prospect.orgKYCDetails.entitiesOutsideUAE"
                            title="Delete"
                          />
                        </FlexActionItem>
                      )}
                    </FlexContainer>
                  </Box>
                ))}
                {values.entitiesOutsideUAE.length < limits.OUTSIDE_SUBSIDIARY_COUNT && (
                  <RelativeBox component={Grid} item sm={3} xs={12}>
                    <ArrayAddButton
                      title="Add another"
                      limit={limits.OUTSIDE_SUBSIDIARY_COUNT}
                      requiredFields={["companyName", "country"]}
                      addedItem={{ companyName: "", country: "" }}
                      dataArray={values.entitiesOutsideUAE}
                      arrayHelpers={arrayHelpers}
                    />
                  </RelativeBox>
                )}
              </>
            )}
          </>
        )}
      </FieldArray>
    </React.Fragment>
  );
};

CompanyBranchesAndSubsidiariesComponent.propTypes = {
  entitiesInUAE: PropTypes.array.isRequired,
  entitiesOutsideUAE: PropTypes.array.isRequired,
  updateProspect: PropTypes.func.isRequired,
  isComeFromROScreens: PropTypes.bool.isRequired,
  values: PropTypes.object.isRequired,
  setTouched: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired
};
